import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import useCatalog from '../lib/useCatalog';
import { useCatalogStatus } from '../lib/Catalog';
import { useBrowseService, useServices } from '../lib/services';
import { formatCount } from '../lib/format';
import PosterCard from './PosterCard';
import CatalogNotice from './CatalogNotice';
import ServiceSwitcher from './ServiceSwitcher';

/**
 * Films, Series, and everything at once: one page, scoped by the URL.
 *
 * The hierarchy, top to bottom, is the order people narrow things down in:
 * what kind of thing (Films / Series), where it streams (the service row),
 * then what it is (genre, year) and in what order. Everything rarer - rating,
 * length, language - waits behind "More". Whatever is applied is listed as a
 * removable tag, so nothing is set invisibly.
 *
 * The URL stays the source of truth for every filter, so a page can be shared
 * or bookmarked. The service is the one exception that also outlives the URL:
 * it is the viewer's standing choice (see useBrowseService), carried into any
 * browse page that arrives without one.
 */

/**
 * Every sort key the API documents, in menu order. Only the ones the active
 * provider advertises are offered (capabilities().sorts).
 */
const SORTS = [
  { key: 'trending', label: 'Popular' },
  { key: 'top_rated', label: 'Top rated' },
  { key: 'top_rated_recent', label: 'Top rated, last 5 years' },
  { key: 'most_watched', label: 'Most watched' },
  { key: 'recent', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'az', label: 'A–Z' },
  { key: 'za', label: 'Z–A' },
  { key: 'longest', label: 'Longest' },
  { key: 'shortest', label: 'Shortest' }
];

// Sorts a provider serves but doesn't advertise, so an old link still reads
const INTERNAL_SORT_LABELS = {
  this_month: 'Released this month',
  hidden_gems: 'Hidden gems',
  longest_running: 'Longest running'
};

const sortLabel = (key) => SORTS.find((sort) => sort.key === key)?.label || INTERNAL_SORT_LABELS[key] || key;

const TYPES = [
  { value: 'movie', label: 'Films' },
  { value: 'series', label: 'Series' },
  { value: 'all', label: 'Both' }
];

const RATINGS = [
  { value: '', label: 'Any rating' },
  { value: '6', label: '6+' },
  { value: '7', label: '7+' },
  { value: '7.5', label: '7.5+' },
  { value: '8', label: '8+' }
];

const RUNTIMES = [
  { value: '', label: 'Any length' },
  { value: '90', label: 'Under 90 min' },
  { value: '120', label: 'Under 2 h' },
  { value: '180', label: 'Under 3 h' }
];

const LANGUAGES = [
  { value: '', label: 'Any language' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' }
];

/**
 * A labelled dropdown: the visible face is ours, the popup is the platform's
 * own <select>, so keyboards, screen readers and phone pickers all work.
 */
const Select = ({ label, value, options, onChange, wide = false }) => {
  if (!options.length) return null;
  const current = options.find((option) => option.value === value) || options[0];
  const set = Boolean(value) && value !== options[0].value;
  return (
    <label className={`bsel${set ? ' is-set' : ''}${wide ? ' bsel--wide' : ''}`}>
      <span className="bsel-k">{label}</span>
      <span className="bsel-v">{current.label}</span>
      <span className="chev chev--down" aria-hidden="true" />
      <select value={value} onChange={(event) => onChange(event.target.value)} aria-label={label}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
};

const Discover = () => {
  const [params, setParams] = useSearchParams();
  const [extraPages, setExtraPages] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { ready, can, supportsFilter, supportsSort } = useCatalogStatus();
  const { find } = useServices();
  const [chosenService, setChosenService] = useBrowseService();

  const get = useCallback((key, fallback = '') => params.get(key) || fallback, [params]);

  const type = get('type', 'all');
  const sort = get('sort', 'trending');
  const genre = get('genre');
  const yearFrom = get('yearFrom');
  const yearTo = get('yearTo');
  const ratingMin = get('ratingMin');
  const runtimeMax = get('runtimeMax');
  const language = get('language');
  const provider = get('provider');

  /**
   * Keep the URL and the standing choice in step.
   *
   * A link that names a service (a home row's "explore all") makes it the
   * choice; a page reached without one (the nav's Films) takes the choice in.
   * Clearing goes through setService below, which drops both at once.
   */
  useEffect(() => {
    if (provider && provider !== chosenService) setChosenService(provider);
  }, [provider]);

  useEffect(() => {
    if (!provider && chosenService && supportsFilter('provider')) {
      const next = new URLSearchParams(params);
      next.set('provider', chosenService);
      setParams(next, { replace: true });
    }
  }, [chosenService, provider, supportsFilter]);

  const query = useMemo(() => {
    const next = new URLSearchParams();
    next.set('type', type);
    next.set('sort', sort);
    if (genre) next.set('genre', genre);
    // The window's width belongs to the provider - a decade on TMDB, a single
    // year on Cinemeta - so both bounds are carried as given
    if (yearFrom) {
      next.set('yearFrom', yearFrom);
      next.set('yearTo', yearTo || yearFrom);
    }
    if (ratingMin) next.set('ratingMin', ratingMin);
    if (runtimeMax) next.set('runtimeMax', runtimeMax);
    if (language) next.set('language', language);
    if (provider) next.set('provider', provider);
    return next.toString();
  }, [type, sort, genre, yearFrom, yearTo, ratingMin, runtimeMax, language, provider]);

  // Hold the request until a standing service has been carried into the URL,
  // or the unscoped page would flash first
  const waitingForService = Boolean(chosenService && !provider && supportsFilter('provider'));
  const { data, loading, error, notConfigured } = useCatalog(
    ready && !waitingForService ? `/api/catalog/discover?${query}` : null
  );

  const metaType = type === 'series' ? 'series' : 'movie';
  const genres = useCatalog(supportsFilter('genre') ? `/api/catalog/genres?type=${metaType}` : null);
  const years = useCatalog(supportsFilter('year') ? `/api/catalog/years?type=${metaType}` : null);

  // A new filter set invalidates everything paged in under the old one
  useEffect(() => {
    setExtraPages([]);
    setExhausted(false);
  }, [query]);

  // Rarer filters open themselves when a link arrives with one already set
  useEffect(() => {
    if (ratingMin || runtimeMax || language) setMoreOpen(true);
  }, [ratingMin, runtimeMax, language]);

  const update = (changes) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    // Paging is per filter set, so a filter change must not carry a page over
    next.delete('page');
    setParams(next, { replace: true });
  };

  const setFilter = (key, value) => update({ [key]: value });

  const setService = (id) => {
    setChosenService(id);
    update({ provider: id });
  };

  /**
   * Select a year bucket by its `from` bound, carrying the bucket's own `to`:
   * the provider owns the width ("1994" on Cinemeta, "1990s" on TMDB).
   */
  const setYear = (from) => {
    const bucket = (years.data || []).find((entry) => String(entry.from) === from);
    if (from && bucket) update({ yearFrom: String(bucket.from), yearTo: String(bucket.to) });
    else update({ yearFrom: '', yearTo: '' });
  };

  const clearAll = () => {
    setChosenService('');
    setParams(type === 'all' ? {} : { type }, { replace: true });
  };

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const page = (data?.page || 1) + extraPages.length + 1;
      const res = await axios.get(`/api/catalog/discover?${query}&page=${page}`);
      const rows = res.data.data.items || [];
      // Not every provider reports a page count, so an empty page is the
      // other way of learning there is nothing left
      if (rows.length) setExtraPages((pages) => [...pages, rows]);
      else setExhausted(true);
    } catch {
      // Leave the already-loaded pages in place; the button can be retried
    } finally {
      setLoadingMore(false);
    }
  }, [data, extraPages.length, query]);

  const items = [...(data?.items || []), ...extraPages.flat()];
  const hasMore = data?.totalPages ? (data.page || 1) + extraPages.length < data.totalPages : !exhausted;

  // Infinite scroll: a sentinel well ahead of the foot of the grid pages the
  // next batch in before the viewer reaches it; the button is the fallback
  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || loading) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingMore) loadMore();
      },
      { rootMargin: '1200px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadMore]);

  if (notConfigured) {
    return (
      <div className="page">
        <CatalogNotice />
      </div>
    );
  }

  const genreOptions = [
    { value: '', label: 'All genres' },
    ...(genres.data || []).map((g) => ({ value: g.name, label: g.name }))
  ];
  const yearBuckets = years.data || [];
  const yearOptions = [
    { value: '', label: 'Any year' },
    ...yearBuckets.map((bucket) => ({ value: String(bucket.from), label: bucket.label }))
  ];
  const sortOptions = SORTS.filter((option) => supportsSort(option.key)).map((option) => ({
    value: option.key,
    label: option.label
  }));
  if (!sortOptions.some((option) => option.value === sort)) {
    sortOptions.push({ value: sort, label: sortLabel(sort) });
  }

  const service = provider ? find(provider) : null;
  const kind = type === 'series' ? 'Series' : type === 'movie' ? 'Films' : 'Films & series';
  const yearLabel = yearBuckets.find((bucket) => String(bucket.from) === yearFrom)?.label || yearFrom;

  // Everything applied, each removable on its own
  const tags = [
    service && { key: 'provider', label: service.name, clear: () => setService('') },
    genre && { key: 'genre', label: genre, clear: () => setFilter('genre', '') },
    yearFrom && { key: 'year', label: yearLabel, clear: () => setYear('') },
    ratingMin && { key: 'rating', label: `Rated ${ratingMin}+`, clear: () => setFilter('ratingMin', '') },
    runtimeMax && {
      key: 'runtime',
      label: RUNTIMES.find((r) => r.value === runtimeMax)?.label || `Under ${runtimeMax} min`,
      clear: () => setFilter('runtimeMax', '')
    },
    language && {
      key: 'language',
      label: LANGUAGES.find((l) => l.value === language)?.label || language,
      clear: () => setFilter('language', '')
    }
  ].filter(Boolean);

  const moreCount = [ratingMin, runtimeMax, language].filter(Boolean).length;
  const hasMoreFilters = supportsFilter('rating') || supportsFilter('runtime') || supportsFilter('language');
  const busy = loading || waitingForService;

  return (
    <div className="page browse">
      <header className="browse-head">
        <div className="browse-titles">
          <h1 className="browse-title">
            {kind}
            {service ? <span className="browse-on"> on {service.name}</span> : null}
          </h1>
          {can('counts') && (
            <span className="browse-count">
              {busy ? ' ' : `${formatCount(data?.total || 0)} titles · ${sortLabel(sort).toLowerCase()}`}
            </span>
          )}
        </div>
        {supportsFilter('type') && (
          <div className="seg browse-type" role="tablist" aria-label="Films or series">
            {TYPES.map((option) => (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={type === option.value}
                className={`seg-btn${type === option.value ? ' is-active' : ''}`}
                onClick={() => update({ type: option.value, genre: '' })}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <ServiceSwitcher value={provider || chosenService} onChange={setService} />

      <div className="browse-bar">
        <div className="browse-controls">
          {supportsFilter('genre') && (
            <Select label="Genre" value={genre} options={genreOptions} onChange={(v) => setFilter('genre', v)} wide />
          )}
          {supportsFilter('year') && yearBuckets.length > 0 && (
            <Select label="Year" value={yearFrom} options={yearOptions} onChange={setYear} />
          )}
          <Select label="Sort" value={sort} options={sortOptions} onChange={(v) => setFilter('sort', v === 'trending' ? '' : v)} />
          {hasMoreFilters && (
            <button
              type="button"
              className={`bmore${moreOpen ? ' is-open' : ''}${moreCount ? ' is-set' : ''}`}
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((open) => !open)}
            >
              More
              {moreCount ? <span className="bmore-n">{moreCount}</span> : null}
              <span className="chev chev--down" aria-hidden="true" />
            </button>
          )}
        </div>

        {moreOpen && hasMoreFilters && (
          <div className="browse-controls browse-controls--more">
            {supportsFilter('rating') && (
              <Select label="Rating" value={ratingMin} options={RATINGS} onChange={(v) => setFilter('ratingMin', v)} />
            )}
            {supportsFilter('runtime') && (
              <Select label="Length" value={runtimeMax} options={RUNTIMES} onChange={(v) => setFilter('runtimeMax', v)} />
            )}
            {supportsFilter('language') && (
              <Select label="Language" value={language} options={LANGUAGES} onChange={(v) => setFilter('language', v)} />
            )}
          </div>
        )}

        {tags.length > 0 && (
          <div className="browse-tags">
            {tags.map((tag) => (
              <button key={tag.key} type="button" className="btag" onClick={tag.clear} aria-label={`Remove ${tag.label}`}>
                {tag.label}
                <span className="btag-x" aria-hidden="true">
                  ×
                </span>
              </button>
            ))}
            <button type="button" className="btag-clear" onClick={clearAll}>
              Clear all
            </button>
          </div>
        )}
      </div>

      {error && <div className="alert">{error}</div>}

      {busy && !items.length ? (
        <div className="browse-grid">
          {Array.from({ length: 18 }, (_, index) => (
            <div key={index} className="skeleton browse-skeleton" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="browse-empty">
          <p>Nothing matches all of that{service ? ` on ${service.name}` : ''}.</p>
          {tags.length > 0 && (
            <button type="button" className="btn btn--soft" onClick={clearAll}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="browse-grid">
          {items.map((title) => (
            <PosterCard
              key={`${title.type}-${title.id}`}
              title={title}
              variant="grid"
              shape="poster"
              caption={title.year ? `${title.name} · ${title.year}` : title.name}
            />
          ))}
        </div>
      )}

      {items.length > 0 && hasMore && (
        <div className="more-row">
          <span ref={sentinel} aria-hidden="true" />
          <button type="button" className="btn btn--soft" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Discover;
